import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  Box,
  TableBody,
  Stack,
  useTheme,
  TableContainer,
  Divider,
  Grid2,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Pagination
} from '@mui/material';
import Loader2 from 'components/Loader2';
import ScrollX from 'components/ScrollX';

import { EmptyTable } from 'components/third-party/ReactTable';
import { ArrowUp2, ArrowDown2 } from 'iconsax-react';
import NotFound from 'pages/extra-pages/not-found';
import { FormattedMessage } from 'react-intl';
import { Column, useTable, HeaderGroup, Cell, useExpanded } from 'react-table';

import { getCookie, setCookie } from 'utils/cookieUtils';
import { useLocation } from 'react-router';

import { FC } from 'react';

function CustomTable({
  icon,
  text,
  link,
  columns,
  data,
  striped,
  sortArray,
  sort,
  setSortingData,
  notFound,
  search = '',
  count,
  size,
  pageIndex,
  handlePageChange,
  handleSizeChange,
  disableButton,
  selectedRowIndex,
  renderRowSubComponent
}: {
  icon?: any;
  text?: any;
  link?: any;
  disableButton?: boolean;
  columns: Column[];
  data: any;
  sortArray: string[];
  sort: any;
  setSortingData: any;
  striped?: boolean;
  notFound?: boolean;
  search?: any;
  count?: any;
  size?: any;
  pageIndex?: any;
  handlePageChange?: any;
  handleSizeChange?: any;
  color?: string;
  selectedRowIndex?: number[];
  renderRowSubComponent?: FC<any>;
}) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data
    },
    useExpanded
  );
  const theme = useTheme();
  const location = useLocation();
  const { pathname } = location;

  const renderTableContent = () => {
    if (rows?.length > 0) {
      return rows?.map((row: any) => {
        prepareRow(row);
        const rowProps = row.getRowProps();
  
        const isRowSelected =
          selectedRowIndex?.includes(row.index) ||
          getCookie('tableSelectedRow') ===
            JSON.stringify({ id: row.cells[0]?.row?.original?._id, path: pathname });

            let backgroundColor;
           if(isRowSelected){
             if(theme.palette.mode === 'dark'){
              backgroundColor =  '#3b698f !important'
             }
             else {
              backgroundColor = '#cee6ff !important'
             }

           }
          else {
            backgroundColor = 'inherit'
          }

        return (
          <>
            <TableRow
              {...row.getRowProps()}
              sx={{
                backgroundColor,
                cursor: 'pointer'
              }}
            >
              {row.cells.map((cell: Cell<{}>) => {
                const cellProps = cell.getCellProps([{ className: cell.column.className }]);
                const { key, ...otherProps } = cellProps;
  
                return (
                  <TableCell
                    onClick={() =>
                      setCookie(
                        'tableSelectedRow',
                        JSON.stringify({ id: row.cells[0]?.row?.original?._id, path: pathname }),
                        0.5
                      )
                    }
                    {...otherProps}
                    style={{
                      padding: 3,
                      paddingLeft: cell.column?.paddingLeft ? cell.column?.paddingLeft : '16px'
                    }}
                    key={key}
                  >
                    {cell.render('Cell')}
                  </TableCell>
                );
              })}
            </TableRow>
            {row.isExpanded && renderRowSubComponent?.({ row, rowProps })}
          </>
        );
      });
    }
  
    if (notFound && (!search || search?.length === 0)) {
      return (
        <TableRow>
          <TableCell
            colSpan={columns.length}
            style={{
              display: 'table-cell',
              verticalAlign: 'middle',
              textAlign: 'center',
              height: '100%',
              fontWeight: 'bold'
            }}
          >
            <NotFound icon={icon} text={text} link={link} disableButton={disableButton} />
          </TableCell>
        </TableRow>
      );
    }
  
    if (!notFound && (search || search?.length > 0)) {
      return <EmptyTable msg={<FormattedMessage id="No Data" />} colSpan={columns.length} />;
    }
  
    if (rows?.length === 0) {
      return <EmptyTable msg={<FormattedMessage id="No Data" />} colSpan={columns.length} />;
    }
  
    return (
      <TableRow>
        <TableCell
          colSpan={columns.length}
          style={{
            display: 'table-cell',
            verticalAlign: 'middle',
            textAlign: 'center',
            height: '20vh',
            fontWeight: 'bold'
          }}
        >
          <Loader2 />
        </TableCell>
      </TableRow>
    );
  };
  

  return (
    <TableContainer>
      <ScrollX style={{ maxHeight: 'calc(100vh - 262px)', overflowY: 'auto' }}>
        <Table stickyHeader {...getTableProps()}>
          <TableHead sx={{ zIndex: 1, position: 'relative' }}>
            {headerGroups?.map((headerGroup: HeaderGroup<{}>) => (
              <TableRow {...headerGroup?.getHeaderGroupProps()}>
                {headerGroup?.headers?.map((column: HeaderGroup<{}>) => {
                  let data = sort && sort?.length > 0 ? sort : [];
                  let val: any = data?.length > 0 && data?.find((item: any) => item.id === column.id);
                  return (
                    <TableCell key={column.id} style={{ position: 'sticky' }}>
                      {column?.id === 'checkbox' ? (
                        <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 'max-content' }}>
                            <FormattedMessage id={String(column?.render('Header'))} />
                          </Box>
                          {sortArray?.includes(column?.id) && (
                            <Stack
                              sx={{ color: 'secondary.light' }}
                              onClick={() => {
                                setSortingData({ name: column?.id });
                                if (val?.sort === true && val?.desc === true) {
                                  val = { ...val, sort: false, desc: false };
                                } else if (val?.sort === true) {
                                  val = { ...val, desc: true };
                                } else {
                                  data = [...data, { id: column?.id, sort: true, desc: false }];
                                }
                              }}
                            >
                              <ArrowUp2
                                size={16}
                                variant="Bold"
                                style={{
                                  color: val?.sort && !val?.desc ? theme.palette.secondary.main : 'inherit'
                                }}
                              />
                              <ArrowDown2
                                size={16}
                                variant="Bold"
                                style={{
                                  marginTop: -8,
                                  color: val?.desc ? theme.palette.secondary.main : 'inherit'
                                }}
                              />
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()} {...(striped && { className: 'striped' })}>
          {renderTableContent()}
          </TableBody>
        </Table>
      </ScrollX>
      {size || count ? <Divider /> : <></>}
      {size || count ? (
        <Box sx={{ padding: '10px' }}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              {size ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="secondary">
                      <FormattedMessage id="Row per page" />
                    </Typography>
                    <FormControl sx={{ m: 1 }}>
                      <Select
                        id="demo-controlled-open-select"
                        value={size}
                        onChange={handleSizeChange}
                        size="small"
                        defaultValue={10}
                        sx={{ '& .MuiSelect-select': { py: 0.75, px: 1.25 } }}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              ) : (
                <></>
              )}
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
              {!count || count === 0 ? (
                <></>
              ) : (
                <Pagination
                  onChange={handlePageChange}
                  count={Math.ceil(count / size)}
                  page={pageIndex}
                  variant="combined"
                  color="primary"
                />
              )}
            </Grid2>
          </Grid2>
        </Box>
      ) : (
        <></>
      )}
    </TableContainer>
  );
};

export default CustomTable;
